import React, { useEffect, useState } from "react";
import { BsFillBagFill } from "react-icons/bs";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAllOrdersOfUser } from "../redux/actions/order";
import { server } from "../server";
import { RxCross1 } from "react-icons/rx";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import axios from "axios";
import { toast } from "react-toastify";
import { logEvent } from "../analytics/ga4";

// Helper to compare variant objects
const variantEquals = (v1, v2) => {
  if (!v1 && !v2) return true;
  if (!v1 || !v2) return false;
  return JSON.stringify(v1) === JSON.stringify(v2);
};

/**
 * Calculates total quantity supplied for a specific cart item across all delivery packages.
 * @param {Array} deliveryPackages - All delivery packages associated with the order.
 * @param {Object} cartItem - The cart item to calculate for.
 * @returns {number} - Total quantity supplied.
 */
const getQuantitySuppliedForItem = (deliveryPackages, cartItem) => {
  let totalSupplied = 0;
  deliveryPackages.forEach((pkg) => {
    (pkg.items || []).forEach((pItem) => {
      if (
        pItem.productId === cartItem.productId._id &&
        variantEquals(pItem.variant, cartItem.variant)
      ) {
        totalSupplied += pItem.quantitySupplied;
      }
    });
  });
  return totalSupplied;
};

const UserOrderDetails = () => {
  const { orders } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false); // For review popup
  const [comment, setComment] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [rating, setRating] = useState(1);

  const { id } = useParams();
  const [cancellationReason, setCancellationReason] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------------------------------
  // For GA4 or analytics (optional):
  // ---------------------------------
  const prepareEcommerceItems = (cart) => {
    return cart.map((item) => ({
      item_id: item.productId._id,
      item_name: item.productId.name,
      price: parseFloat(item.totalPriceWithCommission),
      quantity: item.qty,
      variant: item.variant ? JSON.stringify(item.variant) : "",
    }));
  };

  const sendRefundEvent = (orderObj) => {
    logEvent("refund", {
      currency: "INR",
      transaction_id: orderObj._id,
      value: parseFloat(orderObj.shopTotalOrderPriceWithCommission),
      items: prepareEcommerceItems(orderObj.cart),
      cancellation_reason: cancellationReason,
    });
  };

  // ---------------------------------
  // Fetch the single order by ID for this user
  // ---------------------------------
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (id) {
        try {
          const response = await axios.get(`${server}/order/get-user-order/${id}`, {
            withCredentials: true,
          });
          setOrder(response.data.order);
          setLoading(false);
        } catch (error) {
          console.error("Error fetching order details:", error);
          setError("Failed to fetch order details.");
          setLoading(false);
        }
      }
    };
    fetchOrderDetails();
  }, [id]);

  // If you want all user orders in redux, you can still do:
  // useEffect(() => {
  //   if (user?._id) {
  //     dispatch(getAllOrdersOfUser(user._id));
  //   }
  // }, [dispatch, user]);

  const data = orders && orders.find((item) => item._id === id);

  // ---------------------------------
  // Submit a Review
  // ---------------------------------
  const reviewHandler = async () => {
    try {
      const productId = selectedItem?.productId?._id;
      if (!productId) return;
      const { data } = await axios.put(
        `${server}/product/create-new-review`,
        {
          user,
          rating,
          comment,
          productId,
          orderId: id,
        },
        { withCredentials: true }
      );
      toast.success(data.message);
      dispatch(getAllOrdersOfUser(user._id));
      setComment("");
      setRating(1);
      setOpen(false);
      setSelectedItem((prevItem) => ({ ...prevItem, isReviewed: true }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Error posting review!");
    }
  };

  // ---------------------------------
  // Cancel entire Order
  // (Only if no package created yet)
  // ---------------------------------
  const cancelOrderHandler = async () => {
    if (!order) return;
    // Just an extra safety check
    if (order.deliveryPackages && order.deliveryPackages.length > 0) {
      toast.error("Cannot cancel because a package has already been created!");
      return;
    }
    if (order.status !== "Processing") {
      toast.error(`Order is ${order.status.toLowerCase()} and cannot be cancelled.`);
      return;
    }
    if (!cancellationReason.trim()) {
      toast.error("Please provide a cancellation reason.");
      return;
    }

    try {
      await axios.put(
        `${server}/order/cancel-order/${id}`,
        { cancellationReason },
        { withCredentials: true }
      );
      toast.success("Order successfully cancelled");
      // Optionally log a 'refund' or 'cancel' event
      sendRefundEvent(order);
      dispatch(getAllOrdersOfUser(user._id));
    } catch (error) {
      toast.error(error.response?.data?.message || "Error cancelling order");
    }
  };

  // ---------------------------------
  // Request a Refund if delivered
  // ---------------------------------
  const refundHandler = async () => {
    try {
      const { data } = await axios.put(`${server}/order/order-refund/${id}`, {
        status: "Processing refund",
      });
      toast.success(data.message);
      dispatch(getAllOrdersOfUser(user._id));
    } catch (error) {
      toast.error(error.response?.data?.message || "Error requesting refund");
    }
  };

  // ---------------------------------
  // Download a package invoice
  // (Requires the updated route that user can access)
  // ---------------------------------
  const handleDownloadPackageInvoice = async (pkgId) => {
    try {
      const response = await fetch(
        `${server}/invoice/download-package-invoice/${order._id}/${pkgId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/pdf',
          },
        }
      );
      if (response.ok) {
        const arrayBuffer = await response.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `Package_${pkgId}_Invoice.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const errorText = await response.text();
        console.error('Failed to download package invoice:', errorText);
        toast.error('Failed to download package invoice. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching package invoice:', error);
      toast.error('An error occurred while downloading the package invoice.');
    }
  };

  // ---------------------------------
  // Format variant for display
  // ---------------------------------
  const formatVariant = (variant) => {
    if (!variant || Object.keys(variant).length === 0) return "";
    return Object.entries(variant)
      .map(([key, value]) => `${key}: ${value}`)
      .join(", ");
  };

  // ---------------------------------
  // Render
  // ---------------------------------
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-green-600"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p className="text-xl text-red-500">{error}</p>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p className="text-xl text-red-500">Order not found.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="w-full flex items-center justify-between mb-6">
        <div className="flex items-center">
          <BsFillBagFill size={30} color="#2F855A" />
          <h1 className="pl-2 text-2xl font-semibold">User Order Details</h1>
        </div>
      </div>

      {/* Order Information */}
      <div className="w-full bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
          <h5 className="text-gray-600 mb-2 sm:mb-0">
            <span className="font-medium">Order ID:</span> #{order._id}
          </h5>
          <h5 className="text-gray-600 mb-2 sm:mb-0">
            <span className="font-medium">Placed on:</span>{" "}
            {new Date(order.createdAt).toLocaleDateString()}
          </h5>
          <h5 className="text-green-700 font-semibold">
            <span className="font-medium">Total Price:</span> Rs{" "}
            {Number(order.shopTotalOrderPriceWithoutCommission).toFixed(2)}
          </h5>
        </div>
      </div>

      {/* Order Items */}
      <div className="w-full bg-white rounded-lg shadow p-6 mb-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Item Image
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Item Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Make
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Price Without GST
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Qty (Ordered)
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Qty Supplied
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Balance
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {order.cart.map((item, index) => {
              // Calculate total supplied for this item
              const totalSupplied = getQuantitySuppliedForItem(order.deliveryPackages, item);
              const balance = item.qty - totalSupplied;

              return (
                <tr key={index} className="hover:bg-gray-100">
                  <td className="px-4 py-2 whitespace-nowrap">
                    {item.productId?.images?.length > 0 ? (
                      <img
                        src={item.productId.images[0].url}
                        alt={item.name}
                        className="w-20 h-20 object-contain rounded"
                      />
                    ) : (
                      <img
                        src="/placeholder-image.png"
                        alt="Default"
                        className="w-20 h-20 object-contain rounded"
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {item.productId.name}
                    {item.variant && Object.keys(item.variant).length > 0 && (
                      <span className="text-sm text-gray-500">
                        {" "}
                        ({formatVariant(item.variant)})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                    {item.productId.description}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                    {item.productId.tags}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                  Rs { (item.totalPriceWithCommission / (1 + (item.productId.gstPercentage/100)) ).toFixed(2) }
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {item.qty} {item.productId.pricePerQty}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {totalSupplied}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                    {balance}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delivery Packages (Read-Only) */}
      {order.deliveryPackages && order.deliveryPackages.length > 0 && (
        <div className="w-full bg-white rounded-lg shadow p-6 mb-6 overflow-x-auto">
          <h3 className="text-lg font-semibold mb-4">
            Delivery Packages (Split Shipments)
          </h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Package ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Items
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {order.deliveryPackages.map((pkg) => (
                <tr key={pkg._id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                    {pkg._id}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                    {(pkg.items || []).map((it, i2) => (
                      <div key={i2}>
                        <strong>
                          {it.name}
                          {it.variant && Object.keys(it.variant).length > 0 && (
                            <span className="text-sm text-gray-500">
                              {" ("}
                              {formatVariant(it.variant)}
                              {")"}
                            </span>
                          )}
                        </strong>
                        = {it.quantitySupplied}
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                    {/* User can only view the status, no changes */}
                    {pkg.status}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-700 space-x-2">
                    {/* No Edit, No Delete for user. Only "Download Invoice" if status is "Packed" or "Delivered" */}
                    {(pkg.status === "Packed and ready for dispatch" ||
                      pkg.status === "Delivered") && (
                      <button
                        onClick={() => handleDownloadPackageInvoice(pkg._id)}
                        className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded"
                      >
                        Download Invoice
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review Popup (optional, same as your existing code) */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 sm:w-3/4 lg:w-1/2 p-6 relative">
            <RxCross1
              size={24}
              className="absolute top-4 right-4 text-gray-500 cursor-pointer hover:text-gray-700"
              onClick={() => setOpen(false)}
              aria-label="Close review popup"
            />
            <h2 className="text-2xl font-semibold mb-4 text-center">Give a Review</h2>

            {/* Display selectedItem info */}
            {selectedItem && (
              <div className="flex items-center mb-4">
                <img
                  src={
                    selectedItem?.productId?.images[0]?.url ||
                    "/placeholder-image.png"
                  }
                  alt={selectedItem?.name || "Product"}
                  className="w-20 h-20 object-contain rounded"
                />
                <div className="ml-4">
                  <h3 className="text-lg font-medium">{selectedItem?.name}</h3>
                </div>
              </div>
            )}

            {/* Rating */}
            <div className="mb-4">
              <h5 className="text-lg font-medium mb-2">
                Give a Rating <span className="text-red-500">*</span>
              </h5>
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((i) =>
                  rating >= i ? (
                    <AiFillStar
                      key={i}
                      className="mr-1 cursor-pointer text-yellow-500"
                      size={30}
                      onClick={() => setRating(i)}
                      aria-label={`Rate ${i} stars`}
                    />
                  ) : (
                    <AiOutlineStar
                      key={i}
                      className="mr-1 cursor-pointer text-yellow-500"
                      size={30}
                      onClick={() => setRating(i)}
                      aria-label={`Rate ${i} stars`}
                    />
                  )
                )}
              </div>
            </div>

            {/* Comment */}
            <div className="mb-4">
              <label className="block text-lg font-medium mb-2">
                Write a comment
                <span className="ml-1 text-sm text-gray-500">(optional)</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="How was your product? Share your experience!"
                className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                rows="4"
              />
            </div>

            {/* Submit Button */}
            <div
              className={`w-full bg-green-700 text-white py-2 px-4 rounded-md text-center cursor-pointer hover:bg-green-800 transition-colors duration-200 ${
                rating > 0 ? "" : "opacity-50 pointer-events-none"
              }`}
              onClick={rating > 0 ? reviewHandler : null}
            >
              Submit
            </div>
          </div>
        </div>
      )}

      {/* Shipping + Payment Info */}
      <div className="w-full bg-white rounded-lg shadow p-6 mb-6 flex flex-col md:flex-row md:justify-between">
        <div className="mb-6 md:mb-0">
          <h4 className="text-xl font-semibold mb-2">Shipping Address:</h4>
          <p className="text-gray-700">
            {order.shippingAddress.address1} {order.shippingAddress.address2}
          </p>
          <p className="text-gray-700">
            {order.shippingAddress.city}, {order.shippingAddress.country}
          </p>
          <p className="text-gray-700">{order.user?.phoneNumber}</p>
        </div>
        <div>
          <h4 className="text-xl font-semibold mb-2">Payment Info:</h4>
          <p className="text-gray-700">Payment Mode: {order.paymentMethod}</p>
          {order.status === "Delivered" && (
            <button
              className="mt-4 bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 transition-colors duration-200"
              onClick={refundHandler}
            >
              Get a Refund
            </button>
          )}
        </div>
      </div>

      {/* Cancel order only if no packages exist (deliveryPackages.length === 0) and if status=Processing */}
      {order.status === "Processing" && order.deliveryPackages?.length === 0 && (
        <div className="w-full bg-white rounded-lg shadow p-6 mb-6">
          <h4 className="text-xl font-semibold mb-2">Cancellation Reason:</h4>
          <textarea
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Provide the reason for cancellation"
            className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            rows="4"
          ></textarea>
          <button
            className="mt-4 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors duration-200"
            onClick={cancelOrderHandler}
          >
            Cancel Order
          </button>
        </div>
      )}
    </div>
  );
};

export default UserOrderDetails;
